import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Bell,
  BellOff,
  Check,
  Mic,
  MicOff,
  Phone,
  PhoneOff,
  Plus,
  Share,
  Signal,
  UserCog,
  Users,
  Volume2,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useSignaling } from "./hooks/useSignaling";
import { useWebRTC } from "./hooks/useWebRTC";
import { api, urlBase64ToUint8Array } from "./lib/api";
import type { AuthSession, CallPhase, PublicConfig, SignalEnvelope, User } from "./types";
import "./styles.css";

const SESSION_KEY = "voice-session";

function isStandalone() {
  return window.matchMedia("(display-mode: standalone)").matches ||
    ("standalone" in navigator && (navigator as Navigator & { standalone?: boolean }).standalone === true);
}

function formatDuration(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  return `${String(minutes).padStart(2, "0")}:${String(seconds % 60).padStart(2, "0")}`;
}

export default function App() {
  const [session, setSession] = useState<AuthSession | null>(() => {
    try {
      return JSON.parse(localStorage.getItem(SESSION_KEY) || "null");
    } catch {
      return null;
    }
  });
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [phase, setPhase] = useState<CallPhase>("idle");
  const [callId, setCallId] = useState<string | null>(null);
  const [contacts, setContacts] = useState<User[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<Set<string>>(new Set());
  const [activePeer, setActivePeer] = useState<Pick<User, "id" | "display_name"> | null>(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [micAllowed, setMicAllowed] = useState(false);
  const [pushAllowed, setPushAllowed] = useState(Notification.permission === "granted");
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState("");
  const startedAt = useRef(0);
  const webrtcHandler = useRef<(message: SignalEnvelope) => Promise<void>>(async () => undefined);
  const cleanupCall = useRef<() => void>(() => undefined);

  const onSignal = useCallback(async (message: SignalEnvelope) => {
    if (message.type === "presence") {
      if (!message.from) return;
      setOnlineUsers((current) => {
        const next = new Set(current);
        if (message.payload?.online) next.add(message.from!);
        else next.delete(message.from!);
        return next;
      });
    } else if (message.type === "call") {
      setCallId(message.call_id || null);
      setActivePeer({
        id: message.from || "unknown",
        display_name: String(message.payload?.callerName || message.from || "Собеседник"),
      });
      setPhase("incoming");
    } else if (message.type === "accept") {
      setPhase("connecting");
      await webrtcHandler.current({ type: "accepted", call_id: message.call_id });
    } else if (["offer", "answer", "ice-candidate", "ice-restart"].includes(message.type)) {
      await webrtcHandler.current(message);
    } else if (message.type === "decline" || message.type === "hangup") {
      cleanupCall.current();
      setPhase("idle");
      setCallId(null);
      setActivePeer(null);
    }
  }, []);

  const { connected, send } = useSignaling(session?.access_token || null, onSignal);

  const rtcOptions = useMemo(
    () => ({
      iceServers: config?.iceServers || [],
      sendSignal: send,
      callId,
      onConnected: () => {
        startedAt.current = Date.now();
        setPhase("active");
      },
      onRecovering: () => setPhase("recovering"),
      onFailed: () => setError("Не удалось восстановить аудиосоединение"),
    }),
    [callId, config?.iceServers, send],
  );
  const rtc = useWebRTC(rtcOptions);
  const { handleSignal, makeOffer } = rtc;
  cleanupCall.current = rtc.cleanup;

  useEffect(() => {
    webrtcHandler.current = async (message) => {
      if (message.type === "accepted") await makeOffer();
      else await handleSignal(message);
    };
  }, [handleSignal, makeOffer]);

  useEffect(() => {
    if (!session) return;
    api.config(session.access_token)
      .then((value) => {
        setConfig(value);
        if (
          value.displayName !== session.display_name ||
          value.isAdmin !== session.is_admin
        ) {
          const updated = {
            ...session,
            display_name: value.displayName,
            is_admin: value.isAdmin,
          };
          localStorage.setItem(SESSION_KEY, JSON.stringify(updated));
          setSession(updated);
        }
      })
      .catch(() => {
        localStorage.removeItem(SESSION_KEY);
        setSession(null);
      });
    api.users(session.access_token).then(setContacts).catch(() => undefined);
    api.activeCall(session.access_token).then(async ({ call }) => {
      if (!call) return;
      setCallId(call.id);
      setActivePeer({ id: call.peerId, display_name: call.peerName });
      const params = new URLSearchParams(location.search);
      const notificationAction = params.get("notificationAction");
      history.replaceState({}, "", location.pathname);
      if (notificationAction === "decline") {
        await api.callAction(session.access_token, call.id, "decline").catch(() => undefined);
        setCallId(null);
        setPhase("idle");
        return;
      }
      setPhase(
        call.status === "active"
          ? "connecting"
          : call.callerId === session.user_id
            ? "outgoing"
            : "incoming",
      );
    }).catch(() => undefined);
  }, [session]);

  useEffect(() => {
    if (phase !== "active") return;
    const timer = window.setInterval(
      () => setDuration(Math.floor((Date.now() - startedAt.current) / 1000)),
      1000,
    );
    return () => clearInterval(timer);
  }, [phase]);

  const requestMic = async () => {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: false,
    });
    stream.getTracks().forEach((track) => track.stop());
    setMicAllowed(true);
  };

  const requestPush = async () => {
    if (!config?.vapidPublicKey || !session) throw new Error("VAPID не настроен на сервере");
    const permission = await Notification.requestPermission();
    if (permission !== "granted") throw new Error("Уведомления запрещены");
    const registration = await navigator.serviceWorker.ready;
    const subscription =
      (await registration.pushManager.getSubscription()) ||
      (await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(config.vapidPublicKey),
      }));
    await api.saveSubscription(session.access_token, subscription);
    setPushAllowed(true);
  };

  const beginCall = async (peer: User) => {
    if (!session || !connected) return;
    setError("");
    await rtc.ensurePeer();
    setMicAllowed(true);
    const result = await api.createCall(session.access_token, peer.id);
    setCallId(result.callId);
    setActivePeer(peer);
    setPhase("outgoing");
  };

  const accept = async () => {
    if (!session || !callId) return;
    await rtc.ensurePeer();
    setMicAllowed(true);
    await api.callAction(session.access_token, callId, "accept");
    setPhase("connecting");
  };

  const end = async (action: "decline" | "hangup" = "hangup") => {
    if (session && callId) await api.callAction(session.access_token, callId, action).catch(() => undefined);
    rtc.cleanup();
    setPhase("idle");
    setCallId(null);
    setActivePeer(null);
    setDuration(0);
  };

  useEffect(() => {
    const receive = (event: MessageEvent) => {
      if (event.data?.type === "notification-action" && event.data.callId) {
        setCallId(event.data.callId);
        if (event.data.action === "decline") void end("decline");
        else setPhase("incoming");
      }
    };
    navigator.serviceWorker?.addEventListener("message", receive);
    return () => navigator.serviceWorker?.removeEventListener("message", receive);
  });

  if (!isStandalone()) return <InstallScreen />;
  if (!session) return <LoginScreen onLogin={(value) => {
    localStorage.setItem(SESSION_KEY, JSON.stringify(value));
    setSession(value);
  }} />;

  if (phase === "incoming") {
    const peerName = activePeer?.display_name || "Собеседник";
    return (
      <main className="screen incoming">
        <div className="avatar pulse">{peerName.slice(0, 1)}</div>
        <p className="eyebrow">Входящий звонок</p>
        <h1>{peerName}</h1>
        <div className="call-actions">
          <button className="round danger" onClick={() => void end("decline")} aria-label="Отклонить"><PhoneOff /></button>
          <button className="round success" onClick={() => void accept()} aria-label="Ответить"><Phone /></button>
        </div>
      </main>
    );
  }

  if (phase !== "idle") {
    const peerName = activePeer?.display_name || "Собеседник";
    return (
      <main className="screen call">
        <audio ref={rtc.remoteAudio} autoPlay playsInline />
        <p className="eyebrow">{phase === "active" ? "Защищённый разговор" : phase === "recovering" ? "Восстанавливаем связь…" : "Соединение…"}</p>
        <div className="avatar">{peerName.slice(0, 1)}</div>
        <h1>{peerName}</h1>
        <div className="timer">{phase === "active" ? formatDuration(duration) : "···"}</div>
        <QualityPanel stats={rtc.stats} />
        <div className="call-controls">
          <button className={`round ${rtc.muted ? "active" : ""}`} onClick={rtc.toggleMute} aria-label="Микрофон">
            {rtc.muted ? <MicOff /> : <Mic />}
          </button>
          <button className="round" onClick={() => setError("На iPhone маршрут звука выбирается через Пункт управления")} aria-label="Громкая связь">
            <Volume2 />
          </button>
          <button className="round danger" onClick={() => void end()} aria-label="Завершить"><PhoneOff /></button>
        </div>
        {error && <p className="error">{error}</p>}
      </main>
    );
  }

  return (
    <main className="screen home">
      <header>
        <div>
          <p className="eyebrow">Вы вошли как</p>
          <h1>{session.display_name}</h1>
        </div>
        <div className="header-actions">
          {session.user_id === "user1" && (
            <button className="icon-button" onClick={() => setShowAdmin(!showAdmin)} aria-label="Управление пользователями">
              <UserCog />
            </button>
          )}
          <span className={`connection-dot ${connected ? "online" : ""}`} />
        </div>
      </header>
      {showAdmin && session.user_id === "user1" && (
        <AdminPanel
          token={session.access_token}
          currentUserId={session.user_id}
          onChanged={() => api.users(session.access_token).then(setContacts)}
        />
      )}
      <section className="contacts">
        <div className="section-title">
          <Users />
          <h2>{session.user_id === "user1" ? "Пользователи" : "Основной пользователь"}</h2>
        </div>
        {contacts.length === 0 ? (
          <p className="empty">Нет доступных пользователей</p>
        ) : contacts.map((peer) => (
          <div className="peer-card" key={peer.id}>
            <div className="avatar small">{peer.display_name.slice(0, 1)}</div>
            <div>
              <h2>{peer.display_name}</h2>
              <p>{onlineUsers.has(peer.id) ? "В сети" : "Не в сети — получит уведомление"}</p>
            </div>
            <button className="round success" onClick={() => void beginCall(peer)} disabled={!connected}><Phone /></button>
          </div>
        ))}
      </section>
      <section className="status-list">
        <Status icon={connected ? <Wifi /> : <WifiOff />} label="Сигнализация" ok={connected} />
        <Status icon={pushAllowed ? <Bell /> : <BellOff />} label="Уведомления" ok={pushAllowed} action={!pushAllowed ? requestPush : undefined} />
        <Status icon={micAllowed ? <Mic /> : <MicOff />} label="Микрофон" ok={micAllowed} action={!micAllowed ? requestMic : undefined} />
      </section>
      {error && <p className="error">{error}</p>}
      <button className="logout" onClick={() => {
        localStorage.removeItem(SESSION_KEY);
        setSession(null);
      }}>Сменить пользователя</button>
    </main>
  );
}

function InstallScreen() {
  return (
    <main className="screen install">
      <div className="logo"><Signal /></div>
      <h1>Установите «Связь»</h1>
      <p>На iPhone уведомления о звонках доступны только для приложения, добавленного на экран «Домой».</p>
      <ol>
        <li><span>1</span> Нажмите <Share size={20} /> «Поделиться» в Safari</li>
        <li><span>2</span> Выберите «На экран Домой»</li>
        <li><span>3</span> Откройте приложение с нового значка</li>
      </ol>
    </main>
  );
}

function LoginScreen({ onLogin }: { onLogin: (session: AuthSession) => void }) {
  const [user, setUser] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");
  return (
    <main className="screen login">
      <div className="logo"><Signal /></div>
      <h1>Связь</h1>
      <p>Введите логин и PIN</p>
      <input autoCapitalize="none" autoCorrect="off" autoComplete="username" placeholder="Логин" value={user} onChange={(e) => setUser(e.target.value.toLowerCase().trim())} />
      <input type="password" inputMode="numeric" autoComplete="current-password" placeholder="PIN" value={pin} onChange={(e) => setPin(e.target.value)} />
      <button className="primary" onClick={() => api.login(user, pin).then(onLogin).catch((e) => setError(e.message))}>Войти</button>
      {error && <p className="error">{error}</p>}
    </main>
  );
}

function AdminPanel({
  token,
  currentUserId,
  onChanged,
}: {
  token: string;
  currentUserId: string;
  onChanged: () => Promise<unknown>;
}) {
  const [users, setUsers] = useState<User[]>([]);
  const [id, setId] = useState("");
  const [name, setName] = useState("");
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");

  const reload = useCallback(
    () => api.adminUsers(token).then(setUsers).catch((e) => setError(e.message)),
    [token],
  );
  useEffect(() => { void reload(); }, [reload]);

  const add = async () => {
    setError("");
    await api.createUser(token, {
      id: id.toLowerCase().trim(),
      display_name: name.trim(),
      pin,
    });
    setId("");
    setName("");
    setPin("");
    await Promise.all([reload(), onChanged()]);
  };

  const toggleActive = async (user: User) => {
    setError("");
    await api.updateUser(token, user.id, { is_active: !user.is_active });
    await Promise.all([reload(), onChanged()]);
  };

  return (
    <section className="admin-panel">
      <div className="section-title"><UserCog /><h2>Пользователи</h2></div>
      <div className="admin-form">
        <input autoCapitalize="none" placeholder="Логин: ivan" value={id} onChange={(e) => setId(e.target.value)} />
        <input placeholder="Отображаемое имя" value={name} onChange={(e) => setName(e.target.value)} />
        <input type="password" inputMode="numeric" placeholder="PIN, минимум 4 символа" value={pin} onChange={(e) => setPin(e.target.value)} />
        <button className="primary compact" onClick={() => void add().catch((e) => setError(e.message))} disabled={!id || !name || pin.length < 4}>
          <Plus /> Добавить
        </button>
      </div>
      <div className="admin-users">
        {users.map((user) => (
          <div className="admin-user" key={user.id}>
            <span><strong>{user.display_name}</strong><small>{user.id}{user.id === "user1" ? " · основной" : ""}</small></span>
            <button
              onClick={() => void toggleActive(user).catch((e) => setError(e.message))}
              disabled={user.id === currentUserId}
            >
              {user.is_active ? "Отключить" : "Включить"}
            </button>
          </div>
        ))}
      </div>
      {error && <p className="error">{error}</p>}
    </section>
  );
}

function Status({ icon, label, ok, action }: { icon: React.ReactNode; label: string; ok: boolean; action?: () => Promise<void> }) {
  return (
    <button className="status-row" onClick={() => action?.().catch(() => undefined)} disabled={!action}>
      <span className="status-icon">{icon}</span>
      <span>{label}</span>
      {ok ? <Check className="ok" /> : action ? <span className="setup">Настроить</span> : <span className="bad">Нет связи</span>}
    </button>
  );
}

function QualityPanel({ stats }: { stats: ReturnType<typeof useWebRTC>["stats"] }) {
  return (
    <section className="quality">
      <div><span>Качество</span><strong className={stats.quality}>{stats.quality === "good" ? "Хорошее" : stats.quality === "fair" ? "Среднее" : stats.quality === "poor" ? "Плохое" : "—"}</strong></div>
      <div><span>Потери</span><strong>{stats.packetLoss.toFixed(1)}%</strong></div>
      <div><span>Битрейт</span><strong>{stats.bitrate.toFixed(0)} кбит/с</strong></div>
      <div><span>Задержка</span><strong>{stats.rtt.toFixed(0)} мс</strong></div>
      <div><span>Jitter</span><strong>{stats.jitter.toFixed(0)} мс</strong></div>
      <div><span>Маршрут</span><strong>{stats.mode === "relay" ? "TURN" : stats.mode === "direct" ? "P2P" : "—"}</strong></div>
    </section>
  );
}
